import { useMemo } from 'react';
import { css } from '@emotion/react';
import { Stack, Switch, Typography } from '@mui/material';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { AgTable } from '../../../components/AgTable/AgTable';
import { Loader } from '../../../components/Loader/Loader';
import { useToastMessageState } from '../../../components/ToastMessage/ToastMessageProvider';
import { IArkPesFund } from '../../../data-models/arkpes-manage.data-model';
import { PermissionKey } from '../../../services/PermissionAndRolesKeys';
import { PermissionService } from '../../../services/PermissionService';
import { getErrorMessage } from '../../../services/queryHelpers';
import { useArkPesFundAction, useArkPesFunds } from '../../../services/queries/MaggieArkPesManageQueries';

const sectionCss = css`
  border: 1px solid var(--mui-colors-neutral-30);
  border-radius: 8px;
  padding: 1rem;
`;

export interface ArkPesFundsGridContext {
  onToggle: (fund: IArkPesFund, includeInGl: boolean) => void;
  pendingFundId: number | null;
}

export function IncludeInGlCell(params: ICellRendererParams<IArkPesFund>) {
  const fund = params.data;
  const ctx = params.context as ArkPesFundsGridContext;
  if (!fund) {
    return null;
  }
  return (
    <Switch
      checked={fund.includeInGl}
      disabled={ctx.pendingFundId === fund.id}
      onChange={(e) => ctx.onToggle(fund, e.target.checked)}
      size='small'
      inputProps={{ 'aria-label': `${fund.name || fund.arkpesId} enabled` }}
    />
  );
}

export function ArkPesFundsSection() {
  if (!PermissionService.get().hasPermission(PermissionKey.customerSuccessAll)) {
    return null;
  }
  return <ArkPesFundsGrid />;
}

function ArkPesFundsGrid() {
  const { data: funds, isLoading, isError, error } = useArkPesFunds();
  const { fund } = useArkPesFundAction();
  const { pushErrorToast } = useToastMessageState();

  const handleToggle = (target: IArkPesFund, includeInGl: boolean) => {
    fund.mutate(
      { id: target.id, body: { includeInGl } },
      { onError: (err) => pushErrorToast({ message: getErrorMessage(err, 'Failed to update fund') }) }
    );
  };

  const gridContext: ArkPesFundsGridContext = {
    onToggle: handleToggle,
    pendingFundId: fund.isPending ? (fund.variables?.id ?? null) : null,
  };

  const colDefs = useMemo<ColDef<IArkPesFund>[]>(
    () => [
      { headerName: 'Name', field: 'name', flex: 1 },
      { headerName: 'Full name', field: 'fullName', flex: 1.5 },
      { headerName: 'Fund family', field: 'fundFamily', flex: 1 },
      { headerName: 'Ark id', field: 'arkpesId', flex: 1 },
      {
        headerName: 'Enabled',
        field: 'includeInGl',
        cellRenderer: IncludeInGlCell,
        width: 120,
        suppressHeaderMenuButton: true,
        sortable: false,
      },
    ],
    []
  );

  return (
    <Stack gap='1rem' css={sectionCss} data-testid='arkpes-funds-section'>
      <Stack gap='0.25rem'>
        <Typography variant='h6'>Funds</Typography>
        <Typography variant='body2' color='text.secondary'>
          Newly discovered funds are excluded from the general ledger until enabled here.
        </Typography>
      </Stack>
      {isLoading ? (
        <Loader />
      ) : isError ? (
        <Typography variant='body2' color='error'>
          {getErrorMessage(error, 'Failed to load Ark PES funds')}
        </Typography>
      ) : (
        <Stack height='400px'>
          <AgTable
            rowData={funds}
            columnDefs={colDefs}
            context={gridContext}
            getRowId={(p) => String((p.data as IArkPesFund).id)}
            rowGroupPanelShow='never'
            overlayNoRowsTemplate='No Ark PES funds synced yet.'
          />
        </Stack>
      )}
    </Stack>
  );
}
